import React, { useEffect, useState } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { BottomSheet } from '../ui/BottomSheet';
import type {
  PlanFamilyRange,
  PlanPriceRange,
  PlanSortField,
  PlanStatusFilter,
} from './planTemplateUtils';

export interface PlanTemplateFiltersSheetProps {
  open: boolean;
  onClose: () => void;
  planType: string;
  onPlanTypeChange: (v: string) => void;
  status: PlanStatusFilter;
  onStatusChange: (v: PlanStatusFilter) => void;
  priceRange: PlanPriceRange;
  onPriceRangeChange: (v: PlanPriceRange) => void;
  familyRange: PlanFamilyRange;
  onFamilyRangeChange: (v: PlanFamilyRange) => void;
  sortField: PlanSortField;
  sortDir: 'asc' | 'desc';
  onSortFieldChange: (f: PlanSortField) => void;
  onSortDirChange: (d: 'asc' | 'desc') => void;
  onClear: () => void;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'funeral', label: 'Funeral' },
  { value: 'cremation', label: 'Cremación' },
  { value: 'burial', label: 'Entierro' },
  { value: 'memorial', label: 'Memorial' },
];

const STATUS_OPTIONS: { value: PlanStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
];

const PRICE_OPTIONS: { value: PlanPriceRange; label: string }[] = [
  { value: 'all', label: 'Cualquier precio' },
  { value: 'low', label: 'Menos de $300' },
  { value: 'mid', label: '$300 – $600' },
  { value: 'high', label: 'Más de $600' },
];

const FAMILY_OPTIONS: { value: PlanFamilyRange; label: string }[] = [
  { value: 'all', label: 'Cualquier máximo' },
  { value: 'low', label: '1 a 4 familiares' },
  { value: 'mid', label: '5 a 8 familiares' },
  { value: 'high', label: '9 o más' },
];

const SORT_OPTIONS: { value: PlanSortField; label: string }[] = [
  { value: 'name', label: 'Nombre (A - Z)' },
  { value: 'price', label: 'Precio base' },
  { value: 'family', label: 'Máx. familiares' },
  { value: 'date', label: 'Fecha de creación' },
];

const selectCls =
  'h-11 w-full rounded-xl border border-[#E5EAF1] bg-white px-3 text-sm font-medium text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

export const PlanTemplateFiltersSheet: React.FC<PlanTemplateFiltersSheetProps> = ({
  open,
  onClose,
  planType,
  onPlanTypeChange,
  status,
  onStatusChange,
  priceRange,
  onPriceRangeChange,
  familyRange,
  onFamilyRangeChange,
  sortField,
  sortDir,
  onSortFieldChange,
  onSortDirChange,
  onClear,
}) => {
  const [draft, setDraft] = useState({
    planType,
    status,
    priceRange,
    familyRange,
    sortField,
    sortDir,
  });

  useEffect(() => {
    if (open) {
      setDraft({ planType, status, priceRange, familyRange, sortField, sortDir });
    }
  }, [open, planType, status, priceRange, familyRange, sortField, sortDir]);

  const apply = () => {
    onPlanTypeChange(draft.planType);
    onStatusChange(draft.status);
    onPriceRangeChange(draft.priceRange);
    onFamilyRangeChange(draft.familyRange);
    onSortFieldChange(draft.sortField);
    onSortDirChange(draft.sortDir);
    onClose();
  };

  const clear = () => {
    setDraft({
      planType: '',
      status: 'all',
      priceRange: 'all',
      familyRange: 'all',
      sortField: 'name',
      sortDir: 'asc',
    });
    onClear();
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filtros"
      footer={
        <div className="space-y-2 pb-1">
          <button
            type="button"
            onClick={apply}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 active:scale-[0.99]"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={clear}
            className="flex h-10 w-full items-center justify-center text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            Limpiar filtros
          </button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Tipo</span>
          <select
            className={selectCls}
            value={draft.planType}
            onChange={(e) => setDraft((d) => ({ ...d, planType: e.target.value }))}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Estado</span>
          <select
            className={selectCls}
            value={draft.status}
            onChange={(e) =>
              setDraft((d) => ({ ...d, status: e.target.value as PlanStatusFilter }))
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Rango de precio</span>
          <select
            className={selectCls}
            value={draft.priceRange}
            onChange={(e) =>
              setDraft((d) => ({ ...d, priceRange: e.target.value as PlanPriceRange }))
            }
          >
            {PRICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Máx. familiares</span>
          <select
            className={selectCls}
            value={draft.familyRange}
            onChange={(e) =>
              setDraft((d) => ({ ...d, familyRange: e.target.value as PlanFamilyRange }))
            }
          >
            {FAMILY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Ordenar por</span>
          <div className="flex gap-2">
            <select
              className={`${selectCls} flex-1`}
              value={draft.sortField}
              onChange={(e) =>
                setDraft((d) => ({ ...d, sortField: e.target.value as PlanSortField }))
              }
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                setDraft((d) => ({ ...d, sortDir: d.sortDir === 'asc' ? 'desc' : 'asc' }))
              }
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] bg-white text-slate-600 transition hover:bg-slate-50 active:scale-95"
              title={draft.sortDir === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              <ArrowsUpDownIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default PlanTemplateFiltersSheet;
